import { useEffect, useRef } from 'react';

interface VoiceIndicatorProps {
  isRecording: boolean;
}

const VoiceIndicator = ({ isRecording }: VoiceIndicatorProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const dataArrayRef = useRef<Uint8Array | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    if (!isRecording) {
      // Nếu không ghi âm, clear canvas và dừng audio
      const ctx = canvasRef.current?.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, 20, 50);
      }
      audioContextRef.current?.close();
      audioContextRef.current = null;
      return;
    }

    // Bắt đầu ghi âm
    navigator.mediaDevices.getUserMedia({ audio: true }).then((stream) => {
      mediaStreamRef.current = stream;
      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;

      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 32;
      analyserRef.current = analyser;

      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);

      const bufferLength = analyser.frequencyBinCount;
      dataArrayRef.current = new Uint8Array(bufferLength);

      const canvas = canvasRef.current!;
      const ctx = canvas.getContext('2d')!;
      canvas.width = 20;
      canvas.height = 50;

      const draw = () => {
        if (!isRecording) return; // Dừng vẽ khi không ghi âm nữa
        requestAnimationFrame(draw);
        analyser.getByteFrequencyData(dataArrayRef.current!);

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = '#4CAF50'; // Green

        const numBars = 10; // Số vạch nhỏ hơn
        const barWidth = canvas.width;
        const barSpacing = 1;
        const segmentHeight = canvas.height / numBars - barSpacing;

        for (let i = 0; i < numBars; i++) {
          const value = dataArrayRef.current![i] / 255;
          if (value > 0.3) {
            // Chỉ vẽ nếu giá trị đủ lớn
            ctx.fillRect(0, canvas.height - (i + 1) * (segmentHeight + barSpacing), barWidth, segmentHeight);
          }
        }
      };

      draw();
    });

    return () => {
      mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
      audioContextRef.current?.close();
    };
  }, [isRecording]);

  return <canvas ref={canvasRef} />;
};

export default VoiceIndicator;
